"use client"

import React from 'react'
import { UICheckboxBase as Checkbox } from './ui-checkbox'
import { cn } from '../../lib/utils'
import { CircleCheck } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { UIBox } from '../layout/ui-box'

export interface UICheckboxCardProps extends Omit<React.ComponentProps<typeof Checkbox>, 'checked'> {
  /**
   * 카드 제목
   */
  title: string
  /**
   * 카드 설명
   */
  description?: string
  /**
   * 카드 아이콘 (LucideIcon)
   */
  icon?: LucideIcon
  /**
   * 카드 크기
   */
  size?: 'sm' | 'default' | 'lg'
  /**
   * 카드 변형
   */
  variant?: 'default' | 'card' | 'outlined'
  /**
   * 체크 상태
   */
  checked?: boolean
  /**
   * 체크 상태 변경 핸들러
   */
  onCheckedChange?: (checked: boolean) => void
  /**
   * 컨테이너 클래스명
   */
  className?: string
}

const sizeVariants = {
  sm: {
    container: "p-3",
    icon: "size-4 mb-2",
    title: "text-sm font-medium",
    description: "text-xs text-muted-foreground mt-1",
    indicator: "size-4"
  },
  default: {
    container: "p-4",
    icon: "size-5 mb-3",
    title: "text-base font-medium",
    description: "text-sm text-muted-foreground mt-1",
    indicator: "size-5"
  },
  lg: {
    container: "p-5",
    icon: "size-6 mb-3",
    title: "text-lg font-medium",
    description: "text-base text-muted-foreground mt-1",
    indicator: "size-6"
  }
}

const variantStyles = {
  default: {
    base: "border border-border bg-background",
    hover: "hover:border-accent hover:bg-accent/5",
    focus: "focus-within:border-accent focus-within:ring-2 focus-within:ring-accent/20",
    checked: "border-accent bg-accent/10 ring-2 ring-accent/20"
  },
  card: {
    base: "border border-border bg-card shadow-sm",
    hover: "hover:border-accent hover:bg-accent/5 hover:shadow-md",
    focus: "focus-within:border-accent focus-within:ring-2 focus-within:ring-accent/20",
    checked: "border-accent bg-accent/5 ring-1 ring-accent/30 shadow-md"
  },
  outlined: {
    base: "border-2 border-dashed border-border bg-transparent",
    hover: "hover:border-accent hover:bg-accent/5",
    focus: "focus-within:border-accent focus-within:border-solid",
    checked: "border-accent border-solid bg-accent/10"
  }
}

/**
 * shadcn-ui-blocks 패턴 기반 카드형 체크박스 컴포넌트
 *
 * 주요 개선사항:
 * - 세로 블록 레이아웃 (아이콘 정렬 문제 해결)
 * - 간소화된 구조 및 명확한 variant 차별화
 * - 우상단 체크 인디케이터로 직관적 UI
 */
export const UICheckboxCard = React.forwardRef<
  React.ComponentRef<typeof Checkbox>,
  UICheckboxCardProps
>((
  {
    className,
    title,
    description,
    icon: Icon,
    size = "default",
    variant = "default",
    checked = false,
    onCheckedChange,
    disabled,
    id,
    ...props
  },
  ref
) => {
  const variants = sizeVariants[size]
  const styles = variantStyles[variant]
  const checkboxId = React.useId()
  const finalId = id || checkboxId

  const handleChange = (newChecked: boolean | 'indeterminate') => {
    onCheckedChange?.(newChecked === true)
  }

  return (
    <label
      htmlFor={finalId}
      className={cn(
        "group relative cursor-pointer select-none block",
        disabled && "cursor-not-allowed opacity-60",
        className
      )}
    >
      <UIBox
        className={cn(
          variants.container,
          "rounded-lg transition-[background-color,border-color,box-shadow] duration-200 relative text-start",
          styles.base,
          !disabled && styles.hover,
          !disabled && styles.focus,
          checked && !disabled && styles.checked,
          disabled && "opacity-50 cursor-not-allowed"
        )}
      >
        {/* Hidden native checkbox */}
        <Checkbox
          ref={ref}
          id={finalId}
          checked={checked}
          disabled={disabled}
          className="sr-only"
          onCheckedChange={handleChange}
          {...props}
        />

        {/* Icon Block (Top) */}
        {Icon && (
          <Icon className={cn(
            variants.icon,
            "text-muted-foreground transition-colors",
            "group-hover:text-accent",
            checked && "text-accent"
          )} />
        )}

        {/* Text Content Block */}
        <UIBox className={cn(
          "space-y-1",
          // 체크아이콘 공간 확보를 위한 우측 패딩
          size === 'sm' && "pr-8",
          size === 'default' && "pr-10",
          size === 'lg' && "pr-12"
        )}>
          <UIBox className={cn(
            variants.title,
            "text-foreground transition-colors tracking-tight",
            "group-hover:text-accent",
            checked && "text-accent"
          )}>
            {title}
          </UIBox>

          {description && (
            <UIBox className={cn(variants.description)}>
              {description}
            </UIBox>
          )}
        </UIBox>

        {/* Check Indicator (Top Right) - shadcn-ui-blocks 패턴 */}
        {checked && (
          <UIBox className="absolute top-2 right-2">
            <CircleCheck className={cn(
              "fill-accent text-accent-foreground",
              size === 'sm' && "size-4",
              size === 'default' && "size-5",
              size === 'lg' && "size-6"
            )} />
          </UIBox>
        )}
      </UIBox>
    </label>
  )
})

UICheckboxCard.displayName = "UICheckboxCard"